import { auth } from '../views/firebase';
import { onAuthStateChanged } from 'firebase/auth';

const isJwtLike = (token) => {
  if (!token || typeof token !== 'string') return false;
  return token.split('.').length === 3;
};

const parseJwtPart = (part) => {
  try {
    const base64 = String(part || '').replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const getJwtExpMs = (token) => {
  try {
    if (!isJwtLike(token)) return 0;
    const [, payloadPart] = token.split('.');
    const payload = parseJwtPart(payloadPart);
    if (!payload) return 0;
    const expSeconds = Number(payload?.exp || 0);
    return Number.isFinite(expSeconds) && expSeconds > 0 ? expSeconds * 1000 : 0;
  } catch {
    return 0;
  }
};

const isFirebaseIdTokenLike = (token) => {
  if (!isJwtLike(token)) return false;
  const [headerPart, payloadPart] = token.split('.');
  const header = parseJwtPart(headerPart);
  const payload = parseJwtPart(payloadPart);
  if (!header || !payload) return false;
  const hasKid = typeof header.kid === 'string' && header.kid.length > 0;
  const hasSecureIssuer =
    typeof payload.iss === 'string' &&
    payload.iss.startsWith('https://securetoken.google.com/');
  const hasAud = typeof payload.aud === 'string' && payload.aud.length > 0;
  return hasKid && hasSecureIssuer && hasAud;
};

const isTokenUsable = (token, minValidityMs = 60 * 1000) => {
  if (!isJwtLike(token)) return false;
  const expMs = getJwtExpMs(token);
  if (!expMs) return false;
  return expMs - Date.now() > minValidityMs;
};

const waitForAuthUser = async (timeoutMs = 1500) => new Promise((resolve) => {
  const immediate = auth?.currentUser;
  if (immediate) {
    resolve(immediate);
    return;
  }

  let done = false;
  const finish = (user) => {
    if (done) return;
    done = true;
    resolve(user || null);
  };

  const unsubscribe = onAuthStateChanged(
    auth,
    (user) => {
      unsubscribe();
      finish(user);
    },
    () => {
      unsubscribe();
      finish(null);
    }
  );

  setTimeout(() => {
    unsubscribe();
    finish(auth?.currentUser || null);
  }, timeoutMs);
});

export const getFhirToken = async (forceRefresh = true) => {
  try {
    const currentUser = auth?.currentUser || (await waitForAuthUser());
    if (currentUser?.getIdToken) {
      const token = await currentUser.getIdToken(forceRefresh);
      if (isTokenUsable(token) && isFirebaseIdTokenLike(token)) return token;
    }
  } catch {
    // fall through to empty token; callers should handle missing auth
  }
  // Do not read localStorage tokens here. Some keys (e.g. fbAccessToken)
  // may hold provider OAuth access tokens, not Firebase ID tokens, which
  // causes /api/fhir/* authMiddleware verification to fail with 401.
  return '';
};

export const buildFhirHeaders = async (extra = {}, forceRefresh = true) => {
  const token = await getFhirToken(forceRefresh);
  return {
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};
